import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Sparkles, MapPin, Loader2 } from "lucide-react";

export default function OfferMatches() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState("");
  const [explanations, setExplanations] = useState({});
  const [explaining, setExplaining] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/talent/offers/${offerId}/matches`);
      setMatches(res.data);
    } catch (err) {
      setError(formatApiError(err));
      setMatches([]);
    }
  }, [offerId]);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const explain = async (cvId) => {
    setExplaining(cvId);
    try {
      const res = await api.post("/talent/explain", { offer_id: offerId, cv_id: cvId });
      setExplanations((prev) => ({ ...prev, [cvId]: res.data.explanation }));
    } catch (err) {
      setExplanations((prev) => ({ ...prev, [cvId]: `Erreur : ${formatApiError(err)}` }));
    } finally {
      setExplaining("");
    }
  };

  return (
    <div data-testid="offer-matches-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} data-testid="matches-logo-link" aria-label="Retour au tableau de bord">
            <Logo />
          </button>
          <button
            data-testid="matches-back-button"
            onClick={() => navigate("/dashboard")}
            className="text-sm text-slate-600 hover:text-brand-navy border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-100 transition-colors"
          >
            Tableau de bord
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-brand-navy mb-1 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-brand-turquoise" />
          Matching intelligent
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Score indicatif basé sur les compétences, langues, expérience, mobilité et disponibilité.
          L'IA assiste le recruteur — elle ne prend jamais de décision d'embauche ou de rejet.
        </p>

        {error && (
          <div data-testid="matches-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {matches === null ? (
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
          </div>
        ) : matches.length === 0 ? (
          <p data-testid="matches-empty" className="text-center text-slate-400 bg-white rounded-xl border border-slate-200 p-12">
            Aucun candidat dans le Talent Pool pour le moment.
          </p>
        ) : (
          <div className="space-y-4">
            {matches.map((m) => (
              <div key={m.cv.cv_id} data-testid={`match-card-${m.cv.cv_id}`} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-semibold text-brand-navy">{m.cv.full_name}</p>
                    <p className="text-sm text-slate-500">{m.cv.profession}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {[m.cv.city, m.cv.country].filter(Boolean).join(", ") || "—"} · {m.cv.experience_years} an(s)
                    </p>
                  </div>
                  <div className="text-right">
                    <p data-testid={`match-score-${m.cv.cv_id}`} className="text-2xl font-extrabold text-brand-turquoise">{m.score}%</p>
                    <p className="text-xs text-slate-400">correspondance</p>
                  </div>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                  <div
                    className="h-full bg-gradient-to-r from-brand-blue to-brand-turquoise rounded-full"
                    style={{ width: `${m.score}%` }}
                  />
                </div>
                {m.reasons.length > 0 && (
                  <ul className="mb-2 space-y-1">
                    {m.reasons.map((r, i) => (
                      <li key={i} className="text-xs text-green-700 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" /> {r}
                      </li>
                    ))}
                  </ul>
                )}
                {m.gaps.length > 0 && (
                  <ul className="mb-4 space-y-1">
                    {m.gaps.map((g, i) => (
                      <li key={i} className="text-xs text-brand-orange flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" /> Écart identifié : {g}
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  data-testid={`match-explain-${m.cv.cv_id}`}
                  onClick={() => explain(m.cv.cv_id)}
                  disabled={explaining === m.cv.cv_id}
                  className="flex items-center gap-2 text-xs font-medium text-brand-blue border border-brand-blue/30 rounded-lg px-3 py-2 hover:bg-brand-sky transition-colors disabled:opacity-50"
                >
                  {explaining === m.cv.cv_id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  Explication IA
                </button>
                {explanations[m.cv.cv_id] && (
                  <div data-testid={`match-explanation-${m.cv.cv_id}`} className="mt-4 text-sm text-slate-600 bg-brand-sky/50 border border-brand-blue/10 rounded-lg p-4 whitespace-pre-line">
                    {explanations[m.cv.cv_id]}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
